import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Tooltip } from "@mui/material"; import React from "react";
import moment from "moment";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatCurrencyAmount } from "../../../utils/helper";

const CLOSING_BALANCE_DETAIL_ICON_FLAGS = 'opening_balance_present';

const periodRangeFromLabel = (label, mode) => {
    if (!label || typeof label !== "string") return null;
    const trimmed = label.trim();
    const monthAbb = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const m = String(mode || "").toLowerCase();

    if (m === "monthly") {
        const monthIdx = monthAbb.findIndex((abbr) => trimmed.toLowerCase().startsWith(abbr));
        const yearMatch = trimmed.match(/(\d{4})/);
        if (monthIdx === -1 || !yearMatch) return null;
        const y = parseInt(yearMatch[1], 10);
        const start = moment({ year: y, month: monthIdx, day: 1 });
        const end = start.clone().endOf("month");
        return { start_date: start.format("YYYY-MM-DD"), end_date: end.format("YYYY-MM-DD") };
    }

    if (m === "quarterly") {
        const qMatch = trimmed.match(/Q(\d)/i);
        const yearMatch = trimmed.match(/(\d{4})/);
        if (!qMatch || !yearMatch) return null;
        const q = parseInt(qMatch[1], 10);
        if (q < 1 || q > 4) return null;
        const y = parseInt(yearMatch[1], 10);
        const startMonth = (q - 1) * 3;
        const start = moment({ year: y, month: startMonth, day: 1 });
        const end = start.clone().add(2, "months").endOf("month");
        return { start_date: start.format("YYYY-MM-DD"), end_date: end.format("YYYY-MM-DD") };
    }

    if (m === "yearly") {
        let y = null;
        const strictYear = trimmed.match(/^(\d{4})$/);
        if (strictYear) {
            y = parseInt(strictYear[1], 10);
        } else {
            const yearMatch = trimmed.match(/\b(19|20)\d{2}\b/);
            if (yearMatch) y = parseInt(yearMatch[0], 10);
        }
        if (y == null || Number.isNaN(y)) return null;
        const start = moment({ year: y, month: 0, day: 1 });
        const end = start.clone().endOf("year");
        return { start_date: start.format("YYYY-MM-DD"), end_date: end.format("YYYY-MM-DD") };
    }

    return null;
};

const CashFlowListing = ({ cashFlowListing, selectedCurrency, currencySymbol, onOpenDetails, handleItemClicked }) => {
    const formatCurrency = (val) => `US$${(val || 0).toLocaleString()}`;
    const cellStyle = { border: "1px solid black", textAlign: "center", padding: "5px" };
    const leftCell = { ...cellStyle, textAlign: "left", };
    const monthAbbrs = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const quarterStartMonths = { 1: 0, 2: 3, 3: 6, 4: 9 }; 
    const now = new Date();
    const currentMonth = now.getMonth(); 
    const currentYear = now.getFullYear();

    const isUpToCurrent = (labelIdx) => {
        const label = cashFlowListing?.labels?.[labelIdx];
        if (!label) return false;

        const yearMatch = label.match(/(\d{4})/);
        if (yearMatch && parseInt(yearMatch[1]) < currentYear) return true;

        const quarterMatch = label.match(/Q(\d)/i);
        if (quarterMatch) {
            const quarter = parseInt(quarterMatch[1]);
            const startMonth = quarterStartMonths[quarter];
            return startMonth !== undefined && currentMonth >= startMonth;
        }

        const monthIdx = monthAbbrs.findIndex(m => label.toLowerCase().startsWith(m));
        if (monthIdx === -1) return true;
        return monthIdx <= currentMonth;
    };
    const displayValue = (val, labelIdx) => isUpToCurrent(labelIdx) ? formatCurrencyAmount(val, selectedCurrency) : "";
    const beginningBalance = cashFlowListing?.data?.find((d) => d.type === "BAL");
    const sections = cashFlowListing?.data?.filter((d) => d.type !== "BAL");
    const closingBalance = cashFlowListing?.data?.find((d) => d.category === "CLOSING BALANCE");

    const listingMode = String(cashFlowListing?.mode || "").toLowerCase();

    const shouldShowClosingDetailIcon = (columnIdx) => {
        if (!onOpenDetails || !closingBalance?.values) return false;
        const flags = closingBalance?.[CLOSING_BALANCE_DETAIL_ICON_FLAGS];
        if (flags == null || !Array.isArray(flags)) return true;
        return Boolean(flags[columnIdx]);
    };

    const handleClosingDetailsClick = (labelIdx) => {
        handleItemClicked('Cashflow Details Icon');
        const label = cashFlowListing?.labels?.[labelIdx];
        const range = periodRangeFromLabel(label, listingMode);
        if (!range || !onOpenDetails) return;
        onOpenDetails({
            start_date: range.start_date,
            end_date: range.end_date,
            mode: listingMode || "monthly",
        });
    };

    return (
        <TableContainer component={Paper} sx={{overflow: 'auto'}}>
            <Table sx={{ borderCollapse: "collapse" }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#dfdbdbbe" }}>
                        <TableCell sx={{ ...cellStyle, fontWeight: "bold", width:'150px' }}> Fiscal Year Overview </TableCell>

                        {cashFlowListing?.labels?.map((label) => (
                            <TableCell key={label} sx={{ ...cellStyle, fontWeight: "bold" }}> {label}<br/><p style={{fontSize:'10px', margin:'0px'}}>{selectedCurrency} {currencySymbol ? `(${currencySymbol})` : ''}</p></TableCell>
                        ))}

                        <TableCell sx={{ ...cellStyle, fontWeight: "bold", backgroundColor: "#95ceb7ff", }} >
                            Year Total
                            <br/><p style={{fontSize:'10px', margin:'0px'}}>{selectedCurrency} {currencySymbol ? `(${currencySymbol})` : ''}</p>
                        </TableCell>
                    </TableRow>
                </TableHead>
               
                {beginningBalance && (
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#dfdbdbbe" }}>
                            <TableCell sx={{ ...cellStyle, fontWeight: "bold" }}>Opening Balance</TableCell>

                            {beginningBalance.values.map((val, idx) => (
                                <TableCell key={idx} sx={cellStyle}> {displayValue(val, idx)||'--'} </TableCell>
                            ))}

                            <TableCell sx={{ ...cellStyle, fontWeight: "bold", backgroundColor: "#95ceb7ff", }} >
                            {formatCurrencyAmount(
    beginningBalance.year_total,
    selectedCurrency // example: USD, INR, EUR
  )}
                                {/* {(beginningBalance.year_total).toFixed(2)} */}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                )}

                <TableBody>
                    {sections?.map((section, sIdx) => (
                        <React.Fragment key={sIdx}>
                            {(section.type === "IN" || section.type === "OUT") &&
                                section.categories?.map((cat, cIdx) => (
                                    <React.Fragment key={cIdx}>
                                        {/* CATEGORY ROW */}
                                        {cat.has_transaction_types || cat.has_transaction_types_sub_category ? <TableRow sx={{ backgroundColor: section.type === "IN" ? "#8DD37C" : "#D8CE78", }} >
                                            <TableCell colSpan={cashFlowListing.labels.length + 2} sx={{ ...leftCell, fontWeight: "bold" }} >
                                                {section.type === "IN" ? "(+)" : "(-)"}{" "}
                                                {cat.name}
                                            </TableCell>
                                        </TableRow> : null}

                                        {/* DIRECT TRANSACTIONS OF CATEGORY */}
                                        {cat.transactions?.map((txn, tIdx) => (
                                            <TableRow key={`direct-${tIdx}`}>
                                                <TableCell sx={{ ...leftCell, paddingLeft: "30px" }} >
                                                    {txn.transaction_type_alias}
                                                </TableCell>

                                                {txn.values.map((val, i) => (
                                                    <TableCell style={{color:  section.type === "IN" ? "" : "#b52b20"}} key={i} sx={cellStyle}> {displayValue(val, i)||'--'} </TableCell>
                                                ))}

                                                <TableCell sx={{ ...cellStyle, fontWeight: "bold", backgroundColor: "#95ceb7ff", }} >
                                                    {formatCurrencyAmount(txn.year_total, selectedCurrency)}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {/* SUBCATEGORIES */}
                                        {cat.subcategories?.map((sub, subIdx) => (
                                            <React.Fragment key={subIdx}>
                                                {/* SUBCATEGORY ROW */}
                                                {sub.name && sub.transactions && (
                                                    <TableRow sx={{ backgroundColor: section.type === "IN" ? "#e0f0e1" : "#f5f0ce", }} >
                                                        <TableCell colSpan={cashFlowListing.labels.length + 2} sx={{ ...leftCell, paddingLeft: "30px", fontWeight: 600,color:  section.type === "IN" ? "" : "#b52b20" }} >
                                                            {sub.name}
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                                {/* SUBCATEGORY TRANSACTIONS */}
                                                {sub.transactions?.map((txn, tIdx) => (
                                                    <TableRow key={`subtxn-${tIdx}`} sx={{ backgroundColor: section.type === "IN" ? "#e0f0e1" : "#f5f0ce", }}>
                                                        <TableCell sx={{ ...leftCell, paddingLeft: "50px",color:  section.type === "IN" ? "" : "#b52b20" }} > {txn.transaction_type_alias} </TableCell>

                                                        {txn.values.map((val, i) => (
                                                            <TableCell style={{color:  section.type === "IN" ? "" : "#b52b20"}} key={i} sx={cellStyle}> {displayValue(val, i)||'--'} </TableCell>
                                                        ))}

                                                        <TableCell sx={{ ...cellStyle, fontWeight: "bold", backgroundColor: "#95ceb7ff", }} >
                                                            {formatCurrencyAmount(txn.year_total, selectedCurrency)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </React.Fragment>
                                ))}

                            {/* TOTAL ROW */}
                            {(section.type === "IN" || section.type === "OUT") && (
                                <TableRow sx={{ backgroundColor: "#e0e0e0" }}>
                                    <TableCell sx={{ ...leftCell, fontWeight: "bold" }}> {section.type === "OUT" ? "Total Cash Outflow" : "Total Cash Inflow"}</TableCell>

                                    {section.values.map((val, i) => (
                                        <TableCell key={i} sx={cellStyle}> {displayValue(val, i)||'--'} </TableCell>
                                    ))}

                                    <TableCell sx={{ ...cellStyle, fontWeight: "bold", backgroundColor: "#95ceb7ff", }} >
                                        {formatCurrencyAmount(section.year_total, selectedCurrency)}
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* NET CASHFLOW */}
                            {section.type === "NET" && closingBalance?.values && (
                                <TableRow sx={{ backgroundColor: "#5f8edaff" }}>
                                    <TableCell sx={leftCell}>Closing balance</TableCell>

                                    {closingBalance.values.map((val, i) => (
                                        <TableCell key={i} sx={cellStyle} style={{color:'white'}}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <span>{displayValue(val, i)||'--'}</span>
                                                {shouldShowClosingDetailIcon(i) ? (
                                                    <Tooltip title="Cashflow Details" arrow placement="top">
                                                        <InfoOutlinedIcon sx={{ cursor: 'pointer', fontSize: '18px' }} onClick={() => handleClosingDetailsClick(i)} />
                                                    </Tooltip>
                                                ) : null}
                                            </Box>
                                        </TableCell>
                                    ))}

                                    <TableCell sx={{ ...cellStyle, fontWeight: "bold", backgroundColor: "#95ceb7ff", color:'white' }} >
                                       {formatCurrencyAmount(closingBalance.year_total, selectedCurrency)}
                                    </TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CashFlowListing;
